import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { ApiError, getActiveOrgId, setActiveOrgId } from '@/api/client'
import {
  getAccess,
  getInit,
  type Access,
  type InitResponse,
  type PublicSideOfflineReason,
} from '@/api/session/access'
import {
  answerMfaChallenge,
  getMe,
  login as signIn,
  logout as signOut,
  updateMe,
  type LoginResponse,
  type UserSummary,
} from '@/api/session/auth'
import type { OrgPermission, PublicFlag } from '@/types/permissions'

/**
 * What the platform opened before anybody signed in, who is signed in, and where they may act.
 *
 * Client state in the sense of `~/projects/base/frontend/architecture.md`: the boot gate and the
 * session identity. Nothing a view renders out of the library lives here — that is TanStack
 * Query's, and the router clears it whenever the organization or the person changes.
 */
export const useSessionStore = defineStore('session', () => {
  const init = ref<InitResponse | null>(null)
  const user = ref<UserSummary | null>(null)
  const access = ref<Access | null>(null)

  let booting: Promise<void> | null = null

  /**
   * The bootstrap and the session probe, asked once. Every navigation waits on it, so a guard
   * never decides on a session nobody asked about. A failure is not kept: the retry the error
   * screen offers asks again.
   */
  function boot(): Promise<void> {
    booting ??= Promise.all([getInit(), probeSession()]).then(
      ([platform, me]) => {
        init.value = platform
        user.value = me
      },
      (error: unknown) => {
        booting = null
        throw error
      },
    )
    return booting
  }

  /** The signed-in account, or `null` for a visitor: a 401 here is an answer, not a failure. */
  async function probeSession(): Promise<UserSummary | null> {
    try {
      return await getMe()
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null
      throw error
    }
  }

  const isAuthenticated = computed(() => user.value !== null)
  const userName = computed(() => user.value?.name || user.value?.email || '')
  const platformName = computed(() => init.value?.platform.name ?? 'Videas')
  /** The white-label brand's logo, where Videas' would be; `null` is Videas' own. */
  const platformLogoUrl = computed(() => init.value?.platform.logo_url ?? null)
  const features = computed(() => new Set<string>(init.value?.features ?? []))
  const orgFeatures = computed(() => new Set<string>(access.value?.org?.features ?? []))
  const permissions = computed(() => new Set<string>(access.value?.org?.permissions ?? []))

  /** Whether a public platform flag is on. A key absent from the bootstrap is off. */
  function hasFeature(flag: PublicFlag): boolean {
    return features.value.has(flag)
  }

  /**
   * Whether a public flag is on for the active organization — its own override, over the
   * platform's, over the default — as `/access` resolved it. Off while no organization is active.
   * A flag an operator switches on for one organization is only ever here, never in the bootstrap.
   */
  function hasOrgFeature(flag: PublicFlag): boolean {
    return orgFeatures.value.has(flag)
  }

  /**
   * Whether the active organization's platform **sells credits** — `GET /access`'s
   * `org.sells_credits` (`docs/plans/billing-ledger.md` D17). `false` is a white-label tenant
   * billed by contract beside the product — metered, never charged, never refused, holding no
   * wallet, and shown no credit surface at all — **and a platform nobody provisioned**, which
   * the server folds in for the same reason: neither is charged and neither has a wallet, so a
   * screen drawn for either is a screen that can only fail.
   *
   * **Not a public flag**, and it is a sibling of {@link hasOrgFeature} rather than a call into
   * it for that reason: the credit regime is configuration, and a flag beside the configuration
   * would be a second source for one truth. **Nor is it inferred** from an empty wallet or a
   * 404 — a balance of zero on a selling platform means *top up*, and the two must not render
   * the same.
   *
   * **`true` while no organization is active**, which is the opposite default from
   * {@link hasOrgFeature} and deliberate: nothing is drawn before `/access` answers, and a
   * default of `false` would blank the money screens for one navigation on every tenant.
   */
  function sellsCredits(): boolean {
    return access.value?.org?.sells_credits ?? true
  }

  /**
   * Whether the active organization's **public side is withheld** — its shares, embeds and
   * channels refused to visitors because its wallet is empty: `GET /access`'s
   * `org.public_side_offline`, the visitor routes' own gate. Every member may read it, where the
   * wallet is a billing manager's alone, and it is what the neutral balance banner rests on
   * (`docs/plans/billing-ui.md` U6). As fresh as the last {@link refreshAccess}.
   */
  function publicSideOffline(): boolean {
    return access.value?.org?.public_side_offline ?? false
  }

  /**
   * Why the public side is withheld, while it is: `wallet_empty`, or `daily_allowance` — a wallet
   * that has never paid and whose public delivery today reached the platform's daily allowance,
   * which reopens at the day's turn. `null` while nothing is withheld.
   */
  function publicSideOfflineReason(): PublicSideOfflineReason | null {
    return access.value?.org?.public_side_offline_reason ?? null
  }

  /**
   * The public delivery the active organization may serve in one UTC day while it has never paid,
   * in bytes — its platform's figure, as the gate that withholds the public side read it. `null`
   * whenever no daily allowance holds it: a platform that sells nothing, a wallet that has paid or
   * holds nothing.
   */
  function dailyDeliveryAllowanceBytes(): number | null {
    return access.value?.org?.daily_delivery_allowance_bytes ?? null
  }

  /**
   * Whether the caller holds a permission in the active organization. The list is the effective
   * set the server checks every request against, so this hides exactly what it would refuse —
   * and it is keyed by permission, never by role name. Organization keys only: a workspace key
   * is never in this set, and the type says so.
   */
  function can(permission: OrgPermission): boolean {
    return permissions.value.has(permission)
  }

  /**
   * Where the caller may act, for the organization `X-Org-Id` names now. A header naming an
   * organization the caller has since left answers 403: the stale pick is dropped and the list
   * asked for again, so the picker can offer what is left.
   */
  async function refreshAccess(): Promise<Access> {
    try {
      access.value = await getAccess()
    } catch (error) {
      const stalePick = error instanceof ApiError && error.status === 403 && getActiveOrgId() !== null
      if (!stalePick) throw error
      setActiveOrgId(null)
      access.value = await getAccess()
    }
    return access.value
  }

  /** Only `established` opened a session; the other two arms ask for more before one exists. */
  function settle(result: LoginResponse): LoginResponse {
    if (result.status === 'established') {
      user.value = result.user
      access.value = null
    }
    return result
  }

  async function login(email: string, password: string): Promise<LoginResponse> {
    return settle(await signIn({ email, password }))
  }

  async function answerChallenge(challenge: string, code: string): Promise<LoginResponse> {
    return settle(await answerMfaChallenge({ challenge, code }))
  }

  /** Re-read the account, after something the server holds about it changed. */
  async function reloadUser(): Promise<void> {
    user.value = await probeSession()
  }

  /** Store the account's preferred locale, so the next sign-in, here or elsewhere, opens in it. */
  async function updateLocale(locale: string): Promise<void> {
    user.value = await updateMe({ locale })
  }

  /** Forget the caller on this side. The cookie is the server's to end: see {@link logout}. */
  function clear(): void {
    user.value = null
    access.value = null
    setActiveOrgId(null)
  }

  /** End the session server-side, and forget it here whether or not the server answered. */
  async function logout(): Promise<void> {
    try {
      await signOut()
    } finally {
      clear()
    }
  }

  return {
    init,
    user,
    access,
    isAuthenticated,
    userName,
    platformName,
    platformLogoUrl,
    hasFeature,
    hasOrgFeature,
    sellsCredits,
    publicSideOffline,
    publicSideOfflineReason,
    dailyDeliveryAllowanceBytes,
    can,
    boot,
    refreshAccess,
    login,
    answerChallenge,
    reloadUser,
    updateLocale,
    clear,
    logout,
  }
})
